import axios from 'axios';
import { PrequalificationDecision } from '../util/appEnum';
import { fetchConversation, updateConversation, editConversationMessage, deleteConversationMessage } from './service-conversation';

class ServiceSupplierController {
  /**
   * getProjects
   * @description Get projects
   * @static
   * @returns {Object.<projects>} -
   * @memberOf Service
   */
  async getSupplierMembers(supplierId) {
    const response = await axios.get('/api/supplier/member', {
      params: { supplierId: supplierId.toString() }
    });
    return response.data;
  }
  async getAuthSupplierMembers(supplierId, isSigningAuthority) {
    const response = await axios.get('/api/supplier/member', {
      params: {
        supplierId: supplierId.toString(),
        isSigningAuthority
      }
    });
    return response.data;
  }

  async saveSupplier(data, files) {
    const formData = new FormData();
    formData.append('json', JSON.stringify(data));

    for (let key in files) {
      const value = files[key];
      formData.append(key, value);
    }
    const response = await axios.post('/api/supplier', formData);
    return response.data;
  }

  async saveSupplierApplication(data, files) {
    const formData = new FormData();
    formData.append('json', JSON.stringify(data));

    for (let key in files) {
      const value = files[key];
      formData.append(key, value);
    }
    const response = await axios.post('/api/supplier/application', formData);
    return response.data;
  }

  async fetchSupplier(params) {
    const response = await axios.get('/api/suppliers', { params });
    return response.data;
  }

  async saveReason(data) {
    const response = await axios.put('/api/supplier/addReason', data);
    return response.data;
  }

  async getSupplieById(id) {
    const response = await axios.get(`/api/area/supplier/${id}`);
    return response.data;
  }

  async updateSupplier(data, files) {
    const formData = new FormData();
    formData.append('json', JSON.stringify(data));

    for (let key in files) {
      const value = files[key];
      formData.append(key, value);
    }
    const response = await axios.put('/api/supplier', formData);
    return response.data;
  }

  async addSupplierArea(data) {
    const response = await axios.put('/api/supplier/addArea', data);
    return response.data;
  }

  async removeSupplierArea(data) {
    const response = await axios.put('/api/supplier/removeArea', data);
    return response.data;
  }

  async updateSupplierMember(data) {
    const response = await axios.put('/api/supplier/member', data);
    return response.data;
  }

  async saveSupplierMember(data) {
    const response = await axios.post('/api/supplier/member', data);
    return response.data;
  }

  async getSuppliersMembers(suppliers) {
    const response = await axios.get('/api/supplier/members', {
      params: {
        suppliers: suppliers
      },
    });
    return response.data;
  }

  async deleteSupplier(supplierId) {
    const response = await axios.delete(`/api/suppliers/${supplierId}`);
    return response.data;
  }

  async updateSupplierMembers(supplierSlug, membersToAdd, membersToRemove, membersToUpdate) {
    const response = await axios.put(`/api/suppliers/${supplierSlug}/members`, {
      membersToAdd, membersToRemove, membersToUpdate
    });
    return response.data;
  }

  async getRecommendedSuppliers(areaId, municipality) {
    const response = await axios.get(`/api/suppliers/recommended?areaId=${areaId}&municipality=${municipality}`);
    return response.data;
  }

  async addFeaturedSupplierToRegistry(supplierId) {
    const response = await axios.post(`/api/suppliers/featured-supplier/${supplierId}`);
    return response.data;
  }
  async approveSupplierApplication(supplierId, supplierControlsHTML) {
    const response = await axios.put(`/api/suppliers/${supplierId}/prequalification`, {
      decision: PrequalificationDecision.APPROVED,
      supplierControlsHTML,
    });
    return response.data;
  }

  async rejectSupplierApplication(supplierId, supplierControlsHTML) {
    const response = await axios.put(`/api/suppliers/${supplierId}/prequalification`, {
      decision: PrequalificationDecision.REJECTED,
      supplierControlsHTML,
    });
    return response.data;
  }

  async reprocessSupplierApplication(supplierId) {
    const response = await axios.put(`/api/suppliers/${supplierId}/prequalification`, {
      decision: PrequalificationDecision.INBOX,
    });
    return response.data;
  }

  async fetchRecommendedSupplierAdInfo() {
    const response = await axios.get('/api/supplier-ad-info');
    return response.data;
  }

  async putBlock(supplierId, block, reason) {
    const response = await axios.put(`/api/suppliers/${supplierId}/block`, {
      block,
      reason,
    });
    return response.data;
  }

  async bulkDelete(supplierIds) {
    const response = await axios.post('/api/supplier/bulk-edit/delete', { supplierIds });
    return response.data;
  }

  async bulkApprove(supplierIds, supplierControlsHTML) {
    const response = await axios.post('/api/supplier/bulk-edit/approve', { supplierIds, supplierControlsHTML });
    return response.data;
  }

  async bulkReject(supplierIds, supplierControlsHTML) {
    const response = await axios.post('/api/supplier/bulk-edit/reject', { supplierIds, supplierControlsHTML });
    return response.data;
  }

  async bulkReprocess(supplierIds) {
    const response = await axios.post('/api/supplier/bulk-edit/reprocess', { supplierIds });
    return response.data;
  }

  async bulkBlock(supplierIds, reason) {
    const response = await axios.post('/api/supplier/bulk-edit/block', { supplierIds, reason });
    return response.data;
  }

  async bulkUnblock(supplierIds, reason) {
    const response = await axios.post('/api/supplier/bulk-edit/unblock', { supplierIds, reason });
    return response.data;
  }

  async bulkAddAreas(supplierIds, areaIds) {
    const response = await axios.post('/api/supplier/bulk-edit/add-areas-to-suppliers', { supplierIds, areaIds });
    return response.data;
  }

  async bulkRemoveAreas(supplierIds, areaIds) {
    const response = await axios.post('/api/supplier/bulk-edit/remove-areas-from-suppliers', { supplierIds, areaIds });
    return response.data;
  }

  async bulkGetAllIds(params = {}) {
    const response = await axios.get('/api/supplier/bulk-edit/all-ids', { params });
    return response.data;
  }

  async getSupplierStatisticsByYear(supplierId) {
    const response = await axios.get(`/api/supplier/statistics-by-year/${supplierId}`);
    return response.data;
  }

  async getSupplierStatistics(supplierId, { year, phase } = {}) {
    const params = {};
    if (year) params.year = year;
    if (phase) params.phase = phase;
    const response = await axios.get(`/api/supplier/statistics/${supplierId}`, { params });
    return response.data;
  }

  async getSupplierProjects(supplierId, { year, phase } = {}) {
    const params = {};
    if (year) params.year = year;
    if (phase) params.phase = phase;
    const response = await axios.get(`/api/supplier/projects/${supplierId}`, { params });
    return response.data;
  }

  async getSupplierEconomics(supplierId, { refresh } = {}) {
    const params = {};
    if (refresh) params.refresh = true;
    const response = await axios.get(`/api/supplier/economics/${supplierId}`, { params });
    return response.data;
  }

  async getSupplierComments(supId) {
    return await fetchConversation(`/supplier/${supId}/comments`);
  }

  async updateSupplierComments(supId, content) {
    return await updateConversation(`/supplier/${supId}/comments`, content);
  }

  async editSupplierComment(supId, messageId, content) {
    return await editConversationMessage(`/supplier/${supId}/comments`, messageId, content);
  }

  async deleteSupplierComment(supId, messageId) {
    return await deleteConversationMessage(`/supplier/${supId}/comments`, messageId);
  }
}
export const ServiceSupplier = new ServiceSupplierController();
